import { useState, useEffect } from "react";
import CalendarButton from "../components/atoms/CalendarButton";
import DatePickerField from "../components/molecules/DatePickerField";
import DateNavigator from "../components/molecules/DateNavigator";
import CommentTreeView from "../components/organisms/CommentTreeView";
import CommentCsvDownload from "../components/molecules/CommentCsvDownload";
import { fetchRegiInfo, fetchAllShopsMonthlyReports } from "../services/dailyReportService";

// 日本時間の現在日付を YYYYMMDD 形式で返す
const getJapanDateYYYYMMDD = () => {
  const jst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  const y = jst.getFullYear();
  const m = String(jst.getMonth() + 1).padStart(2, "0");
  const d = String(jst.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
};

// YYYYMMDD 文字列 → Date オブジェクト
const parseYYYYMMDD = (str) =>
  new Date(
    parseInt(str.slice(0, 4), 10),
    parseInt(str.slice(4, 6), 10) - 1,
    parseInt(str.slice(6, 8), 10),
  );

// Date オブジェクト → YYYYMMDD 文字列
const formatYYYYMMDD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
};

/**
 * CommentCheckPage - 全店舗コメント確認画面
 *
 * 日付ナビゲーターで選択した日の店舗・レジ別コメント（DailyReport.Message）を確認する。
 * データは選択日を含む月単位で取得し、同月内の日付移動では再取得しない（OfficeInputPage と同じ月次キャッシュ方式）。
 * コメントCSVは表示中の月の全店舗分をまとめてダウンロードする。
 */
function CommentCheckPage() {
  const [selectedDate, setSelectedDate] = useState(getJapanDateYYYYMMDD);
  const [regiInfo, setRegiInfo] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const yearMonth = selectedDate.slice(0, 6);

  // 月が変わるたびに全店舗データを再取得する
  useEffect(() => {
    setLoading(true);
    setErrorMessage(null);
    Promise.all([fetchRegiInfo(), fetchAllShopsMonthlyReports(yearMonth)])
      .then(([regi, rpt]) => {
        setRegiInfo(regi);
        setReports(rpt);
      })
      .catch((err) => {
        console.error("コメント確認データ取得失敗:", err);
        setErrorMessage(`データ取得に失敗しました: ${err.message}`);
      })
      .finally(() => setLoading(false));
  }, [yearMonth]);

  // 日を delta 日分移動する
  const moveDay = (delta) => {
    const d = parseYYYYMMDD(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(formatYYYYMMDD(d));
  };

  return (
    <div className="p-6">
      {/* ヘッダー: 日付ナビゲーター + コメントCSVダウンロードボタン */}
      <div className="flex items-center gap-4 mb-6">
        <DateNavigator prevLabel="◀ 前日" nextLabel="翌日 ▶" onPrev={() => moveDay(-1)} onNext={() => moveDay(1)}>
          <DatePickerField
            selected={parseYYYYMMDD(selectedDate)}
            onChange={(d) => setSelectedDate(formatYYYYMMDD(d))}
            customInput={<CalendarButton yyyyValue={selectedDate} showDay />}
            dateFormat="yyyy/MM/dd"
            locale="ja"
            dateFormatCalendar="yyyy MMMM"
            calendarClassName="datepicker-date-picker"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            enableTabLoop={false}
          />
        </DateNavigator>
        <CommentCsvDownload
          regiInfo={regiInfo}
          reports={reports}
          yearMonth={yearMonth}
          disabled={loading || reports.length === 0}
        />
      </div>

      {loading && <p className="text-gray-400 text-base">読み込み中...</p>}
      {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}

      {!loading && !errorMessage && regiInfo.length > 0 && (
        <CommentTreeView regiInfo={regiInfo} reports={reports} yyyymmdd={selectedDate} />
      )}
    </div>
  );
}

export default CommentCheckPage;
